import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { trackingAPI } from '../services/api';
import { toast } from 'react-toastify';
import { 
  FiSearch, 
  FiTruck, 
  FiPackage, 
  FiMapPin, 
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiRefreshCw
} from 'react-icons/fi';

const Tracking = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);

  const { data: events, isLoading: eventsLoading } = useQuery(
    'tracking-events',
    () => trackingAPI.getEvents()
  );

  const searchMutation = useMutation(trackingAPI.searchTracking, {
    onSuccess: (data) => {
      setSearchResult(data);
      setSearching(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Tracking number not found');
      setSearchResult(null);
      setSearching(false);
    },
  });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      await searchMutation.mutateAsync(searchTerm.trim());
    } catch (error) {
      // Error handled in mutation
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'created':
        return <FiPackage className="h-5 w-5 text-blue-500" />;
      case 'picked_up':
        return <FiTruck className="h-5 w-5 text-green-500" />;
      case 'in_transit':
        return <FiRefreshCw className="h-5 w-5 text-yellow-500" />;
      case 'at_facility':
        return <FiMapPin className="h-5 w-5 text-purple-500" />;
      case 'out_for_delivery':
        return <FiTruck className="h-5 w-5 text-orange-500" />;
      case 'delivered':
        return <FiCheckCircle className="h-5 w-5 text-green-500" />;
      case 'exception':
        return <FiAlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <FiClock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'delivered':
        return 'bg-green-50 border-green-200';
      case 'exception':
        return 'bg-red-50 border-red-200';
      case 'in_transit':
        return 'bg-yellow-50 border-yellow-200';
      case 'out_for_delivery':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tracking</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track your shipments and view delivery status
        </p>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Track Shipment
          </h3>
          <form onSubmit={handleSearch} className="flex space-x-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter any ID: Master tracking, Box ID, Child tracking, Carrier tracking, or Shipment ID..."
                className="form-input"
                disabled={searching}
              />
            </div>
            <button
              type="submit"
              disabled={searching || !searchTerm.trim()}
              className="btn btn-primary"
            >
              {searching ? (
                <div className="spinner mr-2"></div>
              ) : (
                <FiSearch className="h-4 w-4 mr-2" />
              )}
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Search Results */}
      {searchResult && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Tracking Information
            </h3>
            
            {/* Shipment Details */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {searchResult.shipment_type === 'master' ? 'Master Shipment' : 
                     searchResult.shipment_type === 'box' ? 'Box Shipment' : 'Child Shipment'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {searchResult.shipment_type === 'box' ? (
                      <>
                        Box ID: {searchResult.shipment.box_tracking_number}<br/>
                        {searchResult.shipment.carrier_tracking_number && (
                          <>Tracking Number: {searchResult.shipment.carrier_tracking_number}<br/></>
                        )}
                      </>
                    ) : (
                      `Tracking Number: ${searchResult.shipment.master_tracking_number || searchResult.shipment.child_tracking_number}`
                    )}
                  </p>
                  {(searchResult.shipment_type === 'child' || searchResult.shipment_type === 'box') && searchResult.master_shipment && (
                    <p className="text-sm text-gray-500">
                      Master: {searchResult.master_shipment.master_tracking_number}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`status-badge ${
                    searchResult.shipment.status === 'delivered' ? 'status-delivered' :
                    searchResult.shipment.status === 'in_transit' ? 'status-in-transit' :
                    searchResult.shipment.status === 'exception' ? 'status-exception' :
                    'status-pending'
                  }`}>
                    {searchResult.shipment.status.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Tracking Events Timeline */}
            <div className="space-y-4">
              <h5 className="text-md font-medium text-gray-900">Tracking History</h5>
              {searchResult.tracking_events?.length > 0 ? (
                <div className="space-y-3">
                  {searchResult.tracking_events.map((event, index) => (
                    <div
                      key={event.id}
                      className={`flex items-start p-4 rounded-lg border-l-4 ${getEventColor(event.event_type)}`}
                    >
                      <div className="flex-shrink-0 mr-4">
                        {getEventIcon(event.event_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h6 className="text-sm font-medium text-gray-900 capitalize">
                            {event.event_type.replace('_', ' ')}
                          </h6>
                          <span className="text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {event.location}
                        </p>
                        {event.description && (
                          <p className="text-sm text-gray-500 mt-1">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FiClock className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No tracking events</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Tracking information will appear here once the shipment is processed.
                  </p>
                </div>
              )}
            </div>

            {/* Child Shipments (for master shipments) */}
            {searchResult.shipment_type === 'master' && searchResult.child_shipments?.length > 0 && (
              <div className="mt-8">
                <h5 className="text-md font-medium text-gray-900 mb-4">Child Shipments</h5>
                <div className="space-y-3">
                  {searchResult.child_shipments.map((child) => (
                    <div key={child.child_shipment.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h6 className="text-sm font-medium text-gray-900">
                            {child.child_shipment.child_tracking_number}
                          </h6>
                          <p className="text-sm text-gray-500">
                            {child.child_shipment.item_description}
                          </p>
                        </div>
                        <span className={`status-badge ${
                          child.child_shipment.status === 'delivered' ? 'status-delivered' :
                          child.child_shipment.status === 'in_transit' ? 'status-in-transit' :
                          child.child_shipment.status === 'exception' ? 'status-exception' :
                          'status-pending'
                        }`}>
                          {child.child_shipment.status.replace('_', ' ')}
                        </span>
                      </div>
                      {child.tracking_events?.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {child.tracking_events.slice(0, 2).map((event) => (
                            <div key={event.id} className="flex items-center text-xs text-gray-500">
                              {getEventIcon(event.event_type)}
                              <span className="ml-2">{event.location}</span>
                              <span className="ml-auto">
                                {new Date(event.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Events */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Tracking Events
          </h3>
          {eventsLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner"></div>
            </div>
          ) : events?.results?.length > 0 ? (
            <div className="space-y-3">
              {events.results.slice(0, 10).map((event) => (
                <div
                  key={event.id}
                  className={`flex items-start p-3 rounded-lg border-l-4 ${getEventColor(event.event_type)}`}
                >
                  <div className="flex-shrink-0 mr-3">
                    {getEventIcon(event.event_type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h6 className="text-sm font-medium text-gray-900 capitalize">
                        {event.event_type.replace('_', ' ')}
                      </h6>
                      <span className="text-xs text-gray-500">
                        {new Date(event.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {event.location}
                    </p>
                    {event.description && (
                      <p className="text-sm text-gray-500">
                        {event.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <FiSearch className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Tracking events will appear here as shipments are processed.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tracking;
